<script lang="ts">
  // The paperclip button's menu: choose a file, or search-and-pick one of the user's own
  // activities/workouts to attach as a reference (no upload, just a pointer -- see
  // AttachedExerciseCard for how it's rendered). Used by ThreadComposer for both DMs and comments,
  // since this piece is behaviorally identical between the two (modeled directly on
  // MyFitness/Rankings/ActivityPickerPopover.svelte's debounced search-popover pattern).
  import * as Popover from '$lib/components/ui/popover';
  import { Input } from '$lib/components/ui/input';
  import { ScrollArea } from '$lib/components/ui/scroll-area';
  import Button from '@/lib/components/ui/button/button.svelte';
  import { searchMyActivities, searchMyWorkouts, type ExercisePickerOption } from '@/Util/api';
  import { debouncer } from '@/Util/utils';

  export let onFile: () => void;
  export let onPickExercise: (type: 'activity' | 'workout', path: string, name: string) => void;
  export let disabled: boolean = false;

  type Mode = 'menu' | 'activity' | 'workout';

  let open = false;
  let mode: Mode = 'menu';
  let query = '';
  let results: ExercisePickerOption[] = [];
  let loading = false;
  const searchDebouncer = debouncer(300);

  $: if (!open) {
    mode = 'menu';
    query = '';
    results = [];
  }

  function chooseFile() {
    open = false;
    onFile();
  }

  async function runSearch() {
    loading = true;
    try {
      results = mode === 'activity' ? await searchMyActivities(query) : await searchMyWorkouts(query);
    } finally {
      loading = false;
    }
  }

  function openSearch(next: 'activity' | 'workout') {
    mode = next;
    query = '';
    void runSearch();
  }

  function pick(option: ExercisePickerOption) {
    onPickExercise(mode === 'workout' ? 'workout' : 'activity', option.path, option.name);
    open = false;
  }
</script>

<Popover.Root portal="#shadcn-ui-dialog-target" bind:open>
  <Popover.Trigger asChild let:builder>
    <Button builders={[builder]} variant="ghost" size="icon" class="shrink-0 text-muted-foreground hover:text-primary" {disabled} title="Attach a file, activity, or workout">
      <i class="fa-solid fa-paperclip"></i>
    </Button>
  </Popover.Trigger>
  <Popover.Content class="w-64 p-2" align="start">
    {#if mode === 'menu'}
      <div class="flex flex-col gap-0.5">
        <button type="button" class="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted" on:click={chooseFile}>
          <i class="fa-regular fa-paperclip w-4 shrink-0 text-muted-foreground"></i> Choose File
        </button>
        <button type="button" class="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted" on:click={() => openSearch('activity')}>
          <i class="fa-regular fa-person-running w-4 shrink-0 text-muted-foreground"></i> Attach Activity
        </button>
        <button type="button" class="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted" on:click={() => openSearch('workout')}>
          <i class="fa-regular fa-dumbbell w-4 shrink-0 text-muted-foreground"></i> Attach Workout
        </button>
      </div>
    {:else}
      <div class="mb-2 flex items-center gap-1">
        <Button variant="ghost" size="icon" class="h-6 w-6 shrink-0" on:click={() => (mode = 'menu')} title="Back">
          <i class="fa-regular fa-arrow-left text-xs"></i>
        </Button>
        <span class="text-xs font-medium text-muted-foreground">{mode === 'activity' ? 'Attach an activity' : 'Attach a workout'}</span>
      </div>
      <Input placeholder={mode === 'activity' ? 'Search activities…' : 'Search workouts…'} bind:value={query} on:input={() => searchDebouncer.debounce(runSearch)} />
      <ScrollArea class="mt-2 h-64">
        {#if loading}
          <div class="py-6 text-center text-sm text-muted-foreground">Searching…</div>
        {:else if results.length === 0}
          <div class="py-6 text-center text-sm text-muted-foreground">No {mode === 'activity' ? 'activities' : 'workouts'} found.</div>
        {:else}
          <div class="flex flex-col gap-0.5">
            {#each results as option (option.path)}
              <button type="button" class="flex flex-col items-start rounded-md px-2 py-1.5 text-left hover:bg-muted" on:click={() => pick(option)}>
                <span class="w-full truncate text-sm font-medium">{option.name}</span>
                {#if option.start_date}
                  <span class="text-xs text-muted-foreground">{option.start_date}</span>
                {/if}
              </button>
            {/each}
          </div>
        {/if}
      </ScrollArea>
      <a
        href={mode === 'activity' ? '/activities' : '/workouts'}
        target="_blank"
        rel="noopener noreferrer"
        class="mt-2 flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <i class="fa-regular fa-arrow-up-right-from-square w-4 shrink-0"></i>
        Browse all {mode === 'activity' ? 'activities' : 'workouts'}
      </a>
    {/if}
  </Popover.Content>
</Popover.Root>
